/**
 * Mesh -- the site's Wi-Fi mesh (wireless backhaul) over a chosen AP group.
 * Reached from Wireless > Configure > Mesh (no tab strip) and the Customer > Site services
 * "Mesh" tab (configure/mesh, with the strip; OP#1858).
 *
 * Owner decisions 2026-10-12 (OP#1767): any site, built from an AP group, and testers do it
 * themselves. The page walks exactly the server's safe path:
 *   1. Create (saves a switched-off draft; OP#1802 wording): name, AP group, backhaul channel/width (non-DFS only), max hops. The
 *      SAE key is generated server-side and never shown. A "Mesh readiness" summary and each
 *      AP's readiness (short reason; engineering text under "Technical details") are listed
 *      before anything is created. An AP that needs verification gets "Run read-only
 *      capability check" (org_admin; lower roles are told to ask their network engineer) and
 *      a manual "Record result" fallback -- both work BEFORE a mesh exists. With >= 2 ready
 *      APs the rest are listed as "will be left out" and Create needs an explicit tick. The
 *      tick is bound to the exact left-out set (leftOutAckKey): when a recorded check moves an
 *      AP to Ready, the readiness line, the row, the box and the tick all follow the refetch.
 *      Every AP is labelled "<name or Unnamed AP> · serial" (apLabel) with the model in its own
 *      column -- the controller sends the serial as `name` when the AP has no hostname.
 *   1c. Once a mesh exists, "AP readiness" keeps every AP of its group with its membership
 *      (in the mesh / left out), the same Run/Record capability-check buttons (same role
 *      gating) and, for a left-out AP that is now ready, "Add to mesh": a previewed PATCH that
 *      removes the AP's "exclude" override through the existing update path. It never plans,
 *      applies or pushes; Preview/Apply below stay the only way a change reaches an AP.
 *   2. Enable (desired state only -- nothing is pushed).
 *   3. Preview: the PROPOSED TOPOLOGY first (OP#1803: switch/port -> wired root -> mesh points,
 *      dashed expected links with hop + RSSI, checks by severity, left-out APs, push order with
 *      ~4 min each), then per-AP diff, rollout order, blocking issues and warnings.
 *   4. Dry run (default), then Apply after an explicit acknowledgement. Both stay DISABLED, with
 *      the reasons listed, while any block-severity check or refusal stands.
 *   5. Status: the same topology view with the OBSERVED links solid and every AP whose role or
 *      parent differs from the plan flagged; then role / parent / hops / link quality / last
 *      seen per AP, from the APs' own reports.
 *
 * Reads are site_operator+; every write is org_admin+ server-side (api/v2/mesh.py
 * MESH_WRITE_ROLE) -- the RoleGates below use the same floor.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useSession } from "@/stores/session";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import { DataGrid } from "@/components/data/DataGrid";
import { DiffView } from "@/components/data/DiffView";
import { SiteTabs } from "@/components/site/SiteTabs";
import { ApiError } from "@/lib/api/client";
import {
  useApplyMesh, useCreateMesh, useDeleteMesh, useMeshPlan, useRecordMeshCheck, useRunMeshCheck,
  useSiteMesh, useUpdateMesh,
  type MeshApStatus, type MeshCheckProfile, type MeshItem, type MeshOptionAp, type MeshOptions,
  type MeshPlan, type MeshProposedTopology, type SiteMesh,
} from "@/lib/api/siteMesh";
import {
  ACTION_LABEL, ADD_TO_MESH_NOTE, CREATE_MESH_HELP, CREATE_MESH_LABEL, READINESS_LABEL, ROLE_LABEL,
  VERDICT_LABEL, apLabel, apName, canCreateMesh, channelsForWidth, checkLine, enoughEligible, fmtHops,
  fmtRssi, leftOutAckKey, leftOutAps, meshCheckOffer, meshMemberRows, membershipLine,
  overridesAfterAdd, qualityTone, readinessLine, readinessTone, roleTone, runProgress, runTone,
  toConfigDiff, verdictTone,
} from "@/lib/meshView";
import { applyBlockers, diagramMode } from "@/lib/meshTopology";
import { MeshTopologyPanel } from "@/components/mesh/MeshTopologyDiagram";
import { cn } from "@/lib/cn";

const WRITE_ROLE = "org_admin" as const;

export default function Mesh() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useSiteMesh(siteId);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Mesh" subtitle="Wireless backhaul between APs" />
        <EmptyState
          title="Select a site first"
          hint="A mesh links a group of APs at one site over a dedicated 5 GHz backhaul. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Mesh"
        subtitle={siteName ? `Wireless backhaul in ${siteName}` : "Wireless backhaul between APs"}
      />
      <SiteTabs />
      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading mesh…</div>}
      {data && !data.mesh && <CreateMesh siteId={siteId} options={data.options} />}
      {data?.mesh && (
        <div className="flex flex-col gap-4">
          <MeshSummary siteId={siteId} mesh={data.mesh} options={data.options} />
          <MeshMembers siteId={siteId} mesh={data.mesh} options={data.options} />
          <PreviewApply siteId={siteId} mesh={data.mesh} />
          <MeshStatusTable status={data.status?.aps ?? []} alerts={data.status?.alerts ?? []}
                           stateError={data.status?.error} topology={data.mesh.enabled ? data.status?.proposed_topology ?? null : null} />
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* Shared bits                                                                               */
/* ---------------------------------------------------------------------------------------- */

const FIELD =
  "rounded border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand";

/** Readiness list columns (sm+): AP (name · serial) | Model | Readiness | Why | actions.
 *  Literal class string so Tailwind's JIT keeps it. Below sm the row wraps (flex). */
const READINESS_GRID =
  "sm:grid-cols-[minmax(10rem,1.3fr)_minmax(5rem,0.6fr)_minmax(7rem,auto)_minmax(8rem,2fr)_auto]";

function ItemList({ tone, title, items }: { tone: "critical" | "warn"; title: string; items: MeshItem[] }) {
  if (!items.length) return null;
  return (
    <div
      role={tone === "critical" ? "alert" : undefined}
      className={cn(
        "rounded border p-3 text-sm",
        tone === "critical" ? "border-critical/40 bg-critical-soft text-critical" : "border-warn/40 bg-warn-soft text-warn",
      )}
    >
      <div className="mb-1 font-medium">{title}</div>
      <ul className="list-disc space-y-1 pl-5">
        {items.map((it, i) => (
          <li key={`${it.code}-${it.serial ?? ""}-${i}`}>{it.message}</li>
        ))}
      </ul>
    </div>
  );
}

/** The refusals a 4xx carried (create/apply), rendered as a list; otherwise the error banner. */
function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  const body = error instanceof ApiError ? (error.body as { error?: string; refusals?: MeshItem[] } | null) : null;
  if (body?.refusals?.length) {
    return <ItemList tone="critical" title={body.error ?? "Refused"} items={body.refusals} />;
  }
  return <ErrorBanner error={error} />;
}

/* ---------------------------------------------------------------------------------------- */
/* 1. Create                                                                                 */
/* ---------------------------------------------------------------------------------------- */

function CreateMesh({ siteId, options }: { siteId: string; options: MeshOptions }) {
  const create = useCreateMesh(siteId);
  const [name, setName] = useState("Mesh");
  const [group, setGroup] = useState(options.groups[0]?.tag ?? "");
  const [width, setWidth] = useState(options.defaults.channel_width);
  const [channel, setChannel] = useState(options.defaults.channel);
  const [maxHops, setMaxHops] = useState(options.defaults.max_hops);

  const channels = useMemo(() => channelsForWidth(options.channels, width), [options.channels, width]);
  useEffect(() => {
    if (!channels.includes(channel) && channels.length) setChannel(channels.includes(149) ? 149 : (channels[0] ?? options.defaults.channel));
  }, [channels, channel]);

  const groupAps = options.aps.filter((ap) => ap.groups.includes(group));
  const eligible = groupAps.filter((ap) => ap.eligible).length;
  // Every non-ready AP is LEFT OUT of the mesh (stays wired-only) -- explicitly: the operator
  // ticks an acknowledgement and the server refuses the create without it (OP#1809).
  const leftOut = leftOutAps(groupAps);
  // The tick is stored as the key of the set it acknowledged (group + exact left-out serials),
  // not as a boolean: once a recorded check moves an AP to Ready (or the group changes) the key
  // no longer matches and the tick is void in the same render -- Create can never be enabled
  // for a left-out set the operator did not see.
  const leftOutKey = leftOutAckKey(group, leftOut);
  const [ackedKey, setAckedKey] = useState<string | null>(null);
  const ackLeftOut = ackedKey === leftOutKey;
  const readiness = options.groups.find((g) => g.tag === group)?.readiness;

  if (!options.aps.length) {
    return <EmptyState title="No access points at this site" hint="A mesh needs at least two APs at the site." />;
  }

  return (
    <section className="md-card p-4">
      <h2 className="mb-1 text-base font-semibold text-ink">Create a mesh</h2>
      <p className="mb-4 text-sm text-ink-muted">
        Pick the AP group that should share a wireless backhaul. Nothing is sent to any AP until you
        enable the mesh, preview the change and apply it. The mesh key is generated and stored by
        Meridian and is never displayed.
      </p>

      {!options.groups.length && (
        <div className="mb-3 rounded border border-line bg-surface-2 p-3 text-sm text-ink-muted">
          No AP groups at this site yet. Tag the APs that should mesh (Devices, select APs, Tags) with
          a group name such as <span className="font-mono">mesh</span>, then come back.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Name</span>
          <input value={name} maxLength={64} onChange={(e) => setName(e.target.value)} className={FIELD} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">AP group</span>
          <select value={group} onChange={(e) => setGroup(e.target.value)} className={FIELD}>
            {options.groups.map((g) => (
              <option key={g.tag} value={g.tag}>
                {g.tag} ({g.eligible} of {g.serials.length} ready)
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Channel width</span>
          <select value={width} onChange={(e) => setWidth(Number(e.target.value))} className={FIELD}>
            {options.channel_widths.map((w) => (
              <option key={w} value={w}>{w} MHz{w === options.defaults.channel_width ? " (default)" : ""}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Backhaul channel (5 GHz, non-DFS)</span>
          <select value={channel} onChange={(e) => setChannel(Number(e.target.value))} className={FIELD}>
            {channels.map((c) => (
              <option key={c} value={c}>{c}{c === options.defaults.channel ? " (default)" : ""}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Max hops</span>
          <select value={maxHops} onChange={(e) => setMaxHops(Number(e.target.value))} className={FIELD}>
            {options.max_hops.map((h) => (
              <option key={h} value={h}>{h}{h === 1 ? " (recommended)" : ""}</option>
            ))}
          </select>
        </label>
      </div>

      {group && (
        <div className="mt-4">
          {readiness && (
            <div className="mb-2 text-sm font-medium text-ink" data-testid="mesh-readiness">
              {readinessLine(group, readiness)}
            </div>
          )}
          <ul className="divide-y divide-line rounded border border-line" data-testid="mesh-readiness-list">
            {/* Column headings (sm+); every row uses the same grid so names, models and
                statuses line up whatever the AP is called. */}
            <li
              aria-hidden="true"
              className={cn(READINESS_GRID, "hidden bg-surface-2 px-3 py-1.5 text-xs font-medium text-ink-muted sm:grid")}
            >
              <span>AP</span>
              <span>Model</span>
              <span>Readiness</span>
              <span>Why</span>
              <span />
            </li>
            {groupAps.map((ap) => (
              <ReadinessRow key={ap.serial} siteId={siteId} ap={ap} profile={options.check_profile} />
            ))}
          </ul>
        </div>
      )}

      {group && leftOut.length > 0 && enoughEligible(eligible) && (
        <div className="mt-4 rounded border border-warn/40 bg-warn-soft p-3 text-sm text-warn" data-testid="mesh-left-out">
          <div className="mb-1 font-medium">Will be left out of the mesh (they stay wired-only):</div>
          <ul className="list-disc space-y-1 pl-5">
            {leftOut.map((ap) => (
              <li key={ap.serial}>
                <span className="font-medium">{apLabel(ap)}</span> — {ap.summary}
              </li>
            ))}
          </ul>
          <label className="mt-2 flex items-start gap-2 text-ink">
            <input
              type="checkbox"
              checked={ackLeftOut}
              onChange={(e) => setAckedKey(e.target.checked ? leftOutKey : null)}
              aria-label="Acknowledge the APs left out of the mesh"
            />
            <span>
              I understand {leftOut.length === 1 ? "this AP is" : `these ${leftOut.length} APs are`} not part of the
              mesh. An AP that needs verification can be added after its check passes.
            </span>
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <RoleGate min={WRITE_ROLE} mode="disable" reason="Creating a mesh requires an org admin">
          <Button
            variant="primary"
            disabled={create.isPending || !group || !canCreateMesh(eligible, leftOut.length, ackLeftOut)}
            onClick={() =>
              create.mutate({
                name: name.trim() || "Mesh",
                group,
                channel,
                channel_width: width,
                max_hops: maxHops,
                acknowledge_exclusions: ackLeftOut ? leftOut.map((ap) => ap.serial) : [],
              })
            }
          >
            {create.isPending ? "Creating…" : CREATE_MESH_LABEL}
          </Button>
        </RoleGate>
        <span className="text-xs text-ink-muted" data-testid="mesh-create-help">{CREATE_MESH_HELP}</span>
        {group && !enoughEligible(eligible) && (
          <span className="text-xs text-warn">
            A mesh needs at least two ready APs. Verify the APs marked “Needs verification” first.
          </span>
        )}
        {group && enoughEligible(eligible) && leftOut.length > 0 && !ackLeftOut && (
          <span className="text-xs text-ink-muted">Tick the acknowledgement above to create the mesh.</span>
        )}
      </div>
      <div className="mt-3">
        <MutationError error={create.error} />
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* 1b. One AP's readiness row: plain summary first, engineering detail behind a disclosure.   */
/* ---------------------------------------------------------------------------------------- */

/** Pull the server's `manual_fallback` flag + message out of a failed check. */
function checkFailure(error: unknown): { message: string; manual: boolean } | null {
  if (!error) return null;
  const body = error instanceof ApiError ? (error.body as { error?: string; manual_fallback?: boolean } | null) : null;
  return {
    message: body?.error ?? (error instanceof Error ? error.message : "The check failed."),
    manual: body?.manual_fallback ?? false,
  };
}

/** The read-only commands an engineer runs by hand for the manual record (same as the
 *  automatic check, condensed). */
const MANUAL_COMMANDS =
  "opkg list-installed | grep -E 'batman|batctl|wpad'\niw phy | grep -E '^Wiphy|mesh point|5[1-8][0-9]{2} MHz'";

function ReadinessRow({
  siteId, ap, profile, badge, children,
}: {
  siteId: string;
  ap: MeshOptionAp;
  profile: MeshCheckProfile;
  /** Existing mesh only: "In mesh" / "Left out" next to the readiness pill. */
  badge?: ReactNode;
  /** Existing mesh only: the "Add to mesh" control + its preview, under the row. */
  children?: ReactNode;
}) {
  const run = useRunMeshCheck(siteId);
  const record = useRecordMeshCheck(siteId);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualResult, setManualResult] = useState<"pass" | "fail">("pass");
  const [note, setNote] = useState("");
  const failure = checkFailure(run.error);
  const showManual = manualOpen || !!failure?.manual;
  // The row says "needs a check" where one could change the verdict (an "unknown" AP not yet
  // ready); the BUTTON only when the server says this caller may run it (can_check is computed
  // per caller from MESH_WRITE_ROLE). Older controllers sent only can_check.
  const { needsCheck, mayRun } = meshCheckOffer(ap);

  return (
    <li className="flex flex-col gap-1 px-3 py-2 text-sm" data-testid={`mesh-ap-${ap.serial}`}>
      <div className={cn(READINESS_GRID, "flex flex-wrap items-center gap-2 sm:grid")}>
        {/* "<name or Unnamed AP> · serial": the controller sends the serial as `name` when the
            AP has no hostname, so the raw serial is never shown AS a name. */}
        <span className="flex min-w-0 flex-col" data-testid="mesh-ap-label" title={apLabel(ap)}>
          <span className={cn("truncate font-medium", apName(ap) ? "text-ink" : "italic text-ink-muted")}>
            {apName(ap) ?? "Unnamed AP"}
          </span>
          <span className="font-mono text-xs text-ink-subtle">{ap.serial}</span>
        </span>
        <span className="text-xs text-ink-muted" data-testid="mesh-ap-model">{ap.model ?? "—"}</span>
        <span className="flex flex-wrap items-center gap-1">
          {badge}
          <StatusPill status={readinessTone(ap.status)}>{READINESS_LABEL[ap.status]}</StatusPill>
          {!ap.online && <StatusPill status="warn">Offline</StatusPill>}
        </span>
        <span className="text-xs text-ink-muted">{ap.summary}</span>
        {needsCheck && !mayRun && (
          <span className="text-xs text-ink-subtle sm:justify-self-end">Ask your network engineer to verify this AP.</span>
        )}
        {/* Keeps the 5th grid cell so rows without actions align with rows that have them. */}
        {!needsCheck && <span className="hidden sm:block" />}
        {needsCheck && mayRun && (
          <span className="flex flex-wrap items-center gap-2 sm:justify-self-end">
            {/* Belt and braces: the server already said this caller may run it. */}
            <RoleGate
              min={WRITE_ROLE}
              mode="hide"
              fallback={<span className="text-xs text-ink-subtle">Ask your network engineer to verify this AP.</span>}
            >
              <span className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  disabled={run.isPending || !ap.online}
                  title={
                    ap.online
                      ? "Reads installed packages and radio modes over the Web CLI. Changes nothing on the AP."
                      : "The AP is offline"
                  }
                  onClick={() => {
                    record.reset();
                    run.mutate(ap.serial);
                  }}
                >
                  {run.isPending ? "Checking…" : "Run read-only capability check"}
                </Button>
                <Button variant="secondary" onClick={() => setManualOpen((v) => !v)}>
                  Record result
                </Button>
              </span>
            </RoleGate>
          </span>
        )}
      </div>

      {children}

      {failure && (
        <p className="text-xs text-critical" role="alert">
          {failure.message}
          {failure.manual && " Nothing was recorded. You can run the check from a shell and record the result below."}
        </p>
      )}
      {run.data && (
        <p className="text-xs text-ink-muted">
          {run.data.record.result === "pass" ? "Check passed and recorded." : "Check failed and was recorded."}{" "}
          {run.data.ap.summary}
        </p>
      )}

      {needsCheck && mayRun && showManual && (
        <RoleGate min={WRITE_ROLE} mode="hide">
          <div className="flex flex-col gap-2 rounded border border-line bg-surface-2 p-2">
            <div className="text-xs text-ink-muted">
              Record the {profile.name} check by hand. Run these read-only commands on the AP and note what they showed:
            </div>
            <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-surface p-2 font-mono text-[11px] text-ink">
              {MANUAL_COMMANDS}
            </pre>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  name={`res-${ap.serial}`}
                  checked={manualResult === "pass"}
                  onChange={() => setManualResult("pass")}
                />
                Pass (all four present)
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  name={`res-${ap.serial}`}
                  checked={manualResult === "fail"}
                  onChange={() => setManualResult("fail")}
                />
                Fail
              </label>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="What was run and what it showed (required)"
              aria-label={`Evidence for ${apLabel(ap)}`}
              className={FIELD}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                disabled={record.isPending || !note.trim()}
                onClick={() =>
                  record.mutate(
                    { serial: ap.serial, result: manualResult, note: note.trim() },
                    {
                      onSuccess: () => {
                        setManualOpen(false);
                        setNote("");
                        run.reset();
                      },
                    },
                  )
                }
              >
                {record.isPending ? "Saving…" : "Save result"}
              </Button>
              <MutationError error={record.error} />
            </div>
          </div>
        </RoleGate>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-ink-subtle">Technical details</summary>
        <div className="mt-1 flex flex-col gap-1 text-ink-muted">
          <div>
            Firmware: <span className="font-mono">{ap.firmware ?? "—"}</span> ({ap.firmware_family})
          </div>
          <div>{ap.technical}</div>
          {ap.check && (
            <div className="flex flex-col gap-1">
              <div>
                {checkLine(ap.check)} Profile {ap.check.profile ?? "—"}.
              </div>
              {Object.keys(ap.check.checks).length > 0 && (
                <ul className="list-disc pl-5">
                  {profile.criteria.map((c) => (
                    <li key={c.key}>
                      {ap.check?.checks[c.key] ? "present" : "missing"}: {c.label}
                    </li>
                  ))}
                </ul>
              )}
              {ap.check.evidence && (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-surface-2 p-2 font-mono text-[11px]">
                  {ap.check.evidence}
                </pre>
              )}
            </div>
          )}
        </div>
      </details>
    </li>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* 1c. Existing mesh: readiness per AP, capability check, "Add to mesh"                       */
/* ---------------------------------------------------------------------------------------- */

function MeshMembers({ siteId, mesh, options }: { siteId: string; mesh: SiteMesh; options: MeshOptions }) {
  const rows = meshMemberRows(mesh, options.aps);
  if (!rows.length) return null;
  return (
    <section className="md-card p-4" data-testid="mesh-members">
      <h2 className="mb-1 text-base font-semibold text-ink">AP readiness</h2>
      <p className="mb-2 text-sm text-ink-muted">
        Every AP in {mesh.group ? <>group <span className="font-mono">{mesh.group}</span></> : "the mesh's AP list"}.
        A left-out AP stays wired-only. Once its capability check passes it can be added to the mesh;
        adding only changes the mesh's settings, and nothing reaches the AP until you preview and apply.
      </p>
      <div className="mb-2 text-sm font-medium text-ink" data-testid="mesh-membership">
        {membershipLine(mesh.name, rows)}
      </div>
      <ul className="divide-y divide-line rounded border border-line">
        <li
          aria-hidden="true"
          className={cn(READINESS_GRID, "hidden bg-surface-2 px-3 py-1.5 text-xs font-medium text-ink-muted sm:grid")}
        >
          <span>AP</span>
          <span>Model</span>
          <span>Mesh / readiness</span>
          <span>Why</span>
          <span />
        </li>
        {rows.map((r) => (
          <ReadinessRow
            key={r.ap.serial}
            siteId={siteId}
            ap={r.ap}
            profile={options.check_profile}
            badge={
              <StatusPill status={r.leftOut ? "unknown" : "info"}>{r.leftOut ? "Left out" : "In mesh"}</StatusPill>
            }
          >
            {r.canAdd && <AddToMesh siteId={siteId} mesh={mesh} ap={r.ap} />}
          </ReadinessRow>
        ))}
      </ul>
    </section>
  );
}

/**
 * "Add to mesh" for a left-out AP that is now ready. Two steps: the button opens a PREVIEW of
 * the settings change (per-AP setting Left out -> Automatic, member count before/after, and
 * what does NOT happen), and only "Save: add to mesh" sends the PATCH (role_overrides with this
 * AP's "exclude" removed; the server re-runs its gate). Never a plan, an apply or a push.
 */
function AddToMesh({ siteId, mesh, ap }: { siteId: string; mesh: SiteMesh; ap: MeshOptionAp }) {
  const update = useUpdateMesh(siteId);
  const [open, setOpen] = useState(false);
  const before = Object.values(mesh.role_overrides ?? {}).filter((o) => o === "exclude").length;
  return (
    <RoleGate min={WRITE_ROLE} mode="hide">
      <div className="flex flex-col gap-2" data-testid={`mesh-add-${ap.serial}`}>
        {!open && (
          <div>
            <Button variant="secondary" onClick={() => setOpen(true)}>
              Add to mesh…
            </Button>
          </div>
        )}
        {open && (
          <div className="flex flex-col gap-2 rounded border border-line bg-surface-2 p-3 text-xs" data-testid="mesh-add-preview">
            <div className="font-medium text-ink">Preview: add {apLabel(ap)} to {mesh.name}</div>
            <ul className="list-disc pl-5 text-ink-muted">
              <li>
                Per-AP setting: <span className="font-medium text-ink">Left out</span> →{" "}
                <span className="font-medium text-ink">Automatic</span> (with a cable it is a wired root, without one a mesh point)
              </li>
              <li>
                Left-out APs in this mesh: {before} → {before - 1}
              </li>
            </ul>
            <p className="text-ink-muted">{ADD_TO_MESH_NOTE}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate(
                    { role_overrides: overridesAfterAdd(mesh.role_overrides, ap.serial) },
                    { onSuccess: () => setOpen(false) },
                  )
                }
              >
                {update.isPending ? "Saving…" : "Save: add to mesh"}
              </Button>
              <Button variant="secondary" disabled={update.isPending} onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
            <MutationError error={update.error} />
          </div>
        )}
      </div>
    </RoleGate>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* 2. Summary + edit                                                                         */
/* ---------------------------------------------------------------------------------------- */

function MeshSummary({ siteId, mesh, options }: { siteId: string; mesh: SiteMesh; options: MeshOptions }) {
  const update = useUpdateMesh(siteId);
  const del = useDeleteMesh(siteId);
  const [editing, setEditing] = useState(false);
  const [width, setWidth] = useState(mesh.channel_width);
  const [channel, setChannel] = useState(mesh.channel);
  const channels = channelsForWidth(options.channels, width);
  const deployedCount = Object.keys(mesh.deployed).length;
  const running = mesh.last_run?.status === "running";

  return (
    <section className="md-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink">{mesh.name}</h2>
            <StatusPill status={mesh.enabled ? "healthy" : "unknown"}>{mesh.enabled ? "Enabled" : "Switched off"}</StatusPill>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-ink-muted">AP group</dt>
            <dd className="text-ink">{mesh.group ?? `${mesh.members?.length ?? 0} selected APs`}</dd>
            <dt className="text-ink-muted">Backhaul</dt>
            <dd className="font-mono text-ink">{mesh.band} ch {mesh.channel} / {mesh.channel_width} MHz</dd>
            <dt className="text-ink-muted">Mesh id</dt>
            <dd className="font-mono text-xs text-ink">{mesh.mesh_id}</dd>
            <dt className="text-ink-muted">Security</dt>
            <dd className="text-ink">SAE (WPA3), key stored by Meridian</dd>
            <dt className="text-ink-muted">Max hops</dt>
            <dd className="text-ink">{mesh.max_hops}</dd>
            <dt className="text-ink-muted">On APs</dt>
            <dd className="text-ink">{deployedCount} (version {mesh.version})</dd>
          </dl>
          <p className="mt-2 text-xs text-ink-muted">
            Enabling, disabling or editing only changes the desired state. Use Preview below to see
            exactly what each AP would receive, then apply it.
          </p>
        </div>
        <RoleGate min={WRITE_ROLE} mode="hide" fallback={<span className="text-xs text-ink-subtle">read-only</span>}>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={mesh.enabled ? "secondary" : "primary"}
              disabled={update.isPending || running}
              onClick={() => update.mutate({ enabled: !mesh.enabled })}
            >
              {mesh.enabled ? "Disable" : "Enable"}
            </Button>
            <Button variant="secondary" disabled={running} onClick={() => setEditing((v) => !v)}>
              {editing ? "Close" : "Edit backhaul"}
            </Button>
            <Button
              variant="secondary"
              disabled={update.isPending || running}
              title="Generates a new key. Applying it is a mesh-breaking change (points are pushed first)."
              onClick={() => {
                if (window.confirm("Generate a new mesh key? It reaches the APs only when you apply the next plan.")) {
                  update.mutate({ rotate_key: true });
                }
              }}
            >
              Rotate key
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending || running || deployedCount > 0}
              title={deployedCount > 0 ? "Disable and apply first: APs still carry this mesh" : undefined}
              onClick={() => {
                if (window.confirm(`Delete mesh "${mesh.name}"?`)) del.mutate();
              }}
            >
              Delete
            </Button>
          </div>
        </RoleGate>
      </div>

      {editing && (
        <div className="mt-4 flex flex-wrap items-end gap-3 rounded border border-line bg-surface-2 p-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">Channel width</span>
            <select value={width} onChange={(e) => setWidth(Number(e.target.value))} className={FIELD}>
              {options.channel_widths.map((w) => <option key={w} value={w}>{w} MHz</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">Channel</span>
            <select value={channels.includes(channel) ? channel : channels[0]} onChange={(e) => setChannel(Number(e.target.value))} className={FIELD}>
              {channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button
            variant="primary"
            disabled={update.isPending}
            onClick={() => {
              update.mutate(
                { channel: channels.includes(channel) ? channel : channels[0], channel_width: width },
                { onSuccess: () => setEditing(false) },
              );
            }}
          >
            Save
          </Button>
          <span className="text-xs text-ink-muted">A channel change is mesh-breaking: the plan pushes points first, then roots.</span>
        </div>
      )}
      <div className="mt-3 flex flex-col gap-2">
        <MutationError error={update.error} />
        <MutationError error={del.error} />
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* 3+4. Preview, dry run, apply                                                              */
/* ---------------------------------------------------------------------------------------- */

function PreviewApply({ siteId, mesh }: { siteId: string; mesh: SiteMesh }) {
  const plan = useMeshPlan(siteId);
  const apply = useApplyMesh(siteId);
  const [ack, setAck] = useState(false);
  // A plan on screen describes the mesh AS IT WAS previewed. After "Add to mesh" (or any other
  // edit) it is stale: drop it and the apply tick so Apply can only follow a fresh preview.
  const meshKey = `${mesh.version}|${mesh.updated_at ?? ""}|${JSON.stringify(mesh.role_overrides ?? {})}`;
  useEffect(() => {
    plan.reset();
    setAck(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the mesh changes
  }, [meshKey]);
  const run = mesh.last_run;
  const running = run?.status === "running";
  const p: MeshPlan | undefined = plan.data;
  const toPush = p?.aps.filter((a) => a.changed) ?? [];
  const topo = p?.proposed_topology ?? null;
  // Every reason Apply is disabled (block checks first, then other refusals), plain sentences.
  const blockers = p ? applyBlockers(topo, p.refusals) : [];
  // Refusals the topology panel does not already list (e.g. offline AP, render failure).
  const topoBlockKeys = new Set((topo?.checks ?? []).filter((c) => c.severity === "block").map((c) => `${c.code}|${c.serial ?? ""}`));
  const otherRefusals = p?.refusals.filter((r) => !topoBlockKeys.has(`${r.code}|${r.serial ?? ""}`)) ?? [];

  return (
    <section className="md-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Preview and apply</h2>
          <p className="text-sm text-ink-muted">
            Read-only preview of every AP's change. Apply runs a dry run first unless you choose otherwise.
          </p>
        </div>
        <RoleGate min={WRITE_ROLE} mode="disable" reason="Previewing a mesh change requires an org admin">
          <Button
            variant="secondary"
            disabled={plan.isPending || running}
            onClick={() => {
              setAck(false);
              apply.reset();
              plan.mutate();
            }}
          >
            {plan.isPending ? "Building preview…" : "Preview changes"}
          </Button>
        </RoleGate>
      </div>

      {run && (
        <div className="mt-3 rounded border border-line p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-ink">Last rollout</span>
            <StatusPill status={runTone(run)}>{run.status}</StatusPill>
            <span className="text-ink-muted">{runProgress(run)}</span>
          </div>
          <ol className="mt-2 flex flex-col gap-1 text-xs">
            {run.devices.map((d, i) => (
              <li key={d.serial} className="flex flex-wrap items-center gap-2">
                <span className="w-5 text-ink-subtle">{i + 1}.</span>
                <span className="text-ink">{d.name}</span>
                <span className="text-ink-muted">{ACTION_LABEL[d.action]}</span>
                <StatusPill
                  status={d.status === "applied" ? "healthy" : d.status === "failed" ? "critical" : d.status === "running" ? "info" : "unknown"}
                >
                  {d.status}
                </StatusPill>
                {d.reason && <span className="text-ink-muted">{d.reason}</span>}
              </li>
            ))}
          </ol>
          {run.error && <p className="mt-2 text-xs text-critical">{run.error}</p>}
        </div>
      )}

      <div className="mt-3">
        <MutationError error={plan.error} />
      </div>

      {p && (
        <div className="mt-4 flex flex-col gap-3">
          {topo && (
            <section className="rounded border border-line p-3" aria-label="Proposed topology">
              <MeshTopologyPanel topo={topo} mode="plan" title="Step 1 of 2 — Proposed topology" />
            </section>
          )}
          <ItemList tone="critical" title="Blocking issues (nothing can be applied)" items={otherRefusals} />
          <div className="rounded border border-warn/40 bg-warn-soft p-3 text-sm text-warn">
            <div className="font-medium">Outage</div>
            <p>{p.outage_warning}</p>
            {toPush.length > 0 && (
              <p className="mt-1">
                {toPush.length} AP{toPush.length === 1 ? "" : "s"} in this plan; allow about {p.estimated_minutes} minutes.
              </p>
            )}
          </div>
          <ItemList tone="warn" title="Warnings" items={p.warnings} />

          {toPush.length === 0 && !p.refusals.length && (
            <div className="text-sm text-ink-muted">Every AP already matches the mesh settings. Nothing to apply.</div>
          )}

          {toPush.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-ink">Push order (mesh points first, wired roots last)</div>
              {toPush
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((ap) => (
                  <details key={ap.serial} className="rounded border border-line">
                    <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm">
                      <span className="w-5 text-ink-subtle">{(ap.order ?? 0) + 1}.</span>
                      <span className="font-medium text-ink">{ap.name}</span>
                      <span className="text-ink-muted">{ACTION_LABEL[ap.action]}</span>
                      <StatusPill status={roleTone(ap.observed_role)}>{ROLE_LABEL[ap.observed_role ?? "unknown"]}</StatusPill>
                      {ap.expected_role !== "auto" && <span className="text-xs text-ink-subtle">marked {ap.expected_role}</span>}
                      {ap.refusals.length > 0 && <StatusPill status="critical">blocked</StatusPill>}
                    </summary>
                    <div className="p-2">
                      <DiffView diff={toConfigDiff(ap.name, ap.diff)} />
                    </div>
                  </details>
                ))}
            </div>
          )}

          {toPush.length > 0 && (
            <RoleGate min={WRITE_ROLE} mode="hide">
              <div className="flex flex-col gap-2 rounded border border-line bg-surface-2 p-3" data-testid="mesh-apply">
                <div className="text-sm font-medium text-ink">Step 2 of 2 — Apply</div>
                {!p.can_apply && blockers.length > 0 && (
                  <div className="text-sm text-critical" role="alert" data-testid="mesh-apply-blockers">
                    <div className="font-medium">Apply is disabled until these are fixed:</div>
                    <ul className="list-disc space-y-1 pl-5">
                      {blockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    disabled={apply.isPending || !p.can_apply}
                    onClick={() => apply.mutate({ planToken: p.plan_token, dryRun: true })}
                  >
                    Dry run
                  </Button>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={ack} disabled={!p.can_apply} onChange={(e) => setAck(e.target.checked)} />
                    <span>I understand each AP is offline for about 4 minutes, one at a time.</span>
                  </label>
                  <Button
                    variant="primary"
                    disabled={!ack || apply.isPending || !p.can_apply}
                    title={!p.can_apply ? "Fix the blocking issues listed above, then preview again" : undefined}
                    onClick={() => apply.mutate({ planToken: p.plan_token, dryRun: false })}
                  >
                    {apply.isPending ? "Starting…" : `Apply to ${toPush.length} AP${toPush.length === 1 ? "" : "s"}`}
                  </Button>
                </div>
                {apply.data?.dry_run && (
                  <p className="text-sm text-ink-muted">
                    Dry run OK: would push {apply.data.would_push?.length ?? 0} AP(s) in the order shown. Nothing was sent.
                  </p>
                )}
                {apply.data && !apply.data.dry_run && (
                  <p className="text-sm text-healthy">Rollout started. Progress appears above; this page refreshes while it runs.</p>
                )}
                <MutationError error={apply.error} />
              </div>
            </RoleGate>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------------------- */
/* 5. Status                                                                                 */
/* ---------------------------------------------------------------------------------------- */

function MeshStatusTable({
  status, alerts, stateError, topology,
}: {
  status: MeshApStatus[];
  alerts: MeshItem[];
  stateError?: string;
  /** Expected vs observed (OP#1803); null while the mesh is disabled or unavailable. */
  topology: MeshProposedTopology | null;
}) {
  const columns = useMemo<ColumnDef<MeshApStatus, unknown>[]>(
    () => [
      {
        id: "ap",
        header: "AP",
        accessorFn: (r) => r.name,
        cell: (c) => (
          <div className="flex flex-col">
            <span className={cn("font-medium", apName(c.row.original) ? "text-ink" : "italic text-ink-muted")}>
              {apName(c.row.original) ?? "Unnamed AP"}
            </span>
            <span className="font-mono text-xs text-ink-subtle">{c.row.original.serial}</span>
          </div>
        ),
      },
      {
        id: "role",
        header: "Role",
        accessorFn: (r) => r.role,
        cell: (c) => {
          const r = c.row.original;
          return (
            <div className="flex flex-col gap-1">
              <StatusPill status={roleTone(r.role)} title={r.role_note ?? undefined}>{ROLE_LABEL[r.role]}</StatusPill>
              {r.override !== "auto" && <span className="text-xs text-ink-subtle">override: {r.override}</span>}
              {r.role_mismatch && <span className="text-xs text-warn">differs from override</span>}
            </div>
          );
        },
      },
      {
        id: "parent",
        header: "Uplink via",
        accessorFn: (r) => r.parent_name ?? "",
        cell: (c) => {
          const r = c.row.original;
          if (r.hop_count === 0) return <span className="text-sm text-ink">cable</span>;
          return <span className="text-sm text-ink">{r.parent_name ?? "—"}</span>;
        },
      },
      {
        id: "hops",
        header: "Hops",
        accessorFn: (r) => r.hop_count ?? -1,
        cell: (c) => (
          <span className={cn("text-sm", c.row.original.hops_exceeded ? "text-critical" : "text-ink")}>
            {fmtHops(c.row.original.hop_count)}
          </span>
        ),
      },
      {
        id: "link",
        header: "Link",
        accessorFn: (r) => r.link_rssi ?? -200,
        cell: (c) => {
          const r = c.row.original;
          if (!r.parent) return <span className="text-sm text-ink-subtle">—</span>;
          return <StatusPill status={qualityTone(r.quality)}>{fmtRssi(r.link_rssi)}</StatusPill>;
        },
      },
      {
        id: "verdict",
        header: "Mesh support",
        accessorFn: (r) => r.capability.verdict,
        cell: (c) => {
          const r = c.row.original;
          return (
            <StatusPill status={verdictTone(r.capability.verdict, r.eligible)} title={r.blocked_reason ?? r.capability.reason}>
              {r.eligible ? VERDICT_LABEL[r.capability.verdict] : r.override === "exclude" ? "Excluded" : VERDICT_LABEL[r.capability.verdict]}
            </StatusPill>
          );
        },
      },
      {
        id: "deployed",
        header: "Config",
        accessorFn: (r) => (r.deployed ? (r.current ? 2 : 1) : 0),
        cell: (c) => {
          const r = c.row.original;
          if (!r.deployed) return <span className="text-xs text-ink-subtle">not applied</span>;
          return r.current
            ? <span className="text-xs text-ink">current</span>
            : <span className="text-xs text-warn">older version (v{r.deployed_version})</span>;
        },
      },
      {
        id: "seen",
        header: "Last report",
        accessorFn: (r) => r.last_seen ?? "",
        cell: (c) => (
          <span className="text-xs text-ink-muted">
            {c.row.original.last_seen ? new Date(c.row.original.last_seen + (c.row.original.last_seen.endsWith("Z") ? "" : "Z")).toLocaleString() : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <section className="md-card p-4">
      <h2 className="mb-1 text-base font-semibold text-ink">Mesh status</h2>
      <p className="mb-3 text-sm text-ink-muted">
        From the APs' own reports. A wired root has its cable up; a mesh point reaches the LAN over
        the backhaul; an isolated AP has neither and is about to drop off the controller.
      </p>
      {stateError && <p className="mb-2 text-sm text-warn">Live state unavailable ({stateError}).</p>}
      {alerts.length > 0 && (
        <div className="mb-3">
          <ItemList tone="critical" title="Attention" items={alerts} />
        </div>
      )}
      {topology && topology.nodes.length > 0 && (
        <div className="mb-4">
          <MeshTopologyPanel
            topo={topology}
            mode={diagramMode(topology)}
            showPushOrder={false}
            title={diagramMode(topology) === "observed" ? "Topology — what the APs report vs the plan" : "Expected topology (not applied yet)"}
          />
        </div>
      )}
      <DataGrid<MeshApStatus>
        data={status}
        columns={columns}
        rowKey={(r) => r.serial}
        dense
        empty={<span className="text-sm text-ink-muted">No APs in this mesh yet.</span>}
        renderCard={(r) => (
          <div className="flex flex-col gap-1 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-ink">{r.name}</span>
              <StatusPill status={roleTone(r.role)}>{ROLE_LABEL[r.role]}</StatusPill>
            </div>
            <div className="text-xs text-ink-muted">
              {r.hop_count === 0 ? "cable" : `via ${r.parent_name ?? "—"}`} · hops {fmtHops(r.hop_count)} · {fmtRssi(r.link_rssi)}
            </div>
          </div>
        )}
      />
    </section>
  );
}
